// The 2.0 motif (D-77 "forward pass", NN variant 04) as an SVG string, ported from
// the P3-20 round 2 canvas generator (MotifPattern kind NN). No DOM: the web app
// and scripts/build-brand.ts both use it.
//
// A grid of cells sized by a seeded Gaussian "embedding"; a 2→3→2 network drawn in
// the same cells near the focus; in each layer the neuron on the strongest cell
// fires and that route is Ember. Used only in signature spots (landing hero, empty
// states, error pages, the empty mind map) — never behind working UI (D-70, D-79).

export type MotifTheme = 'dark' | 'light';
export type MotifMask = 'none' | 'right' | 'right-soft' | 'bottom' | 'band';

export interface MotifOptions {
  width: number;
  height: number;
  theme: MotifTheme;
  /** Grid pitch; the cell is 12 × pitch / 18 px. Default 28. */
  pitch?: number;
  seed?: number;
  /** Focus of the network, 0–1 of width / height. Defaults 0.7 / 0.5. */
  fx?: number;
  fy?: number;
  /** Unlit (lit: false) folds the network back into the slate substrate (error pages, D-82). */
  lit?: boolean;
  /** Opacity multiplier. */
  dim?: number;
  mask?: MotifMask;
  /** Unique id prefix when several motifs share a page (mask ids). */
  id?: string;
}

const COLOURS = {
  dark: { slate: '#606786', lit: '#E8723A', neuron: '#A9532B', edge: '#7A3D22' },
  light: { slate: '#7D84A5', lit: '#B4501F', neuron: '#D9895F', edge: '#E9B496' },
} as const;

const OPACITY = {
  dark: { a: 0.35, b: 0.55, h: 0.95 },
  light: { a: 0.4, b: 0.6, h: 1 },
} as const;

/** Gradient stops for each mask: [offset, opacity]. */
const MASKS: Record<Exclude<MotifMask, 'none'>, { x2: number; y2: number; stops: Array<[number, number]> }> = {
  right: { x2: 1, y2: 0, stops: [[0.3, 0], [0.62, 1]] },
  'right-soft': { x2: 1, y2: 0, stops: [[0.38, 0], [0.74, 1]] },
  bottom: { x2: 0, y2: 1, stops: [[0, 0], [0.55, 1]] },
  band: { x2: 1, y2: 0, stops: [[0, 0], [0.24, 1], [0.76, 1], [1, 0]] },
};

const LAYERS = [2, 3, 2];

const f1 = (n: number) => Math.round(n * 10) / 10;

function dot(x: number, y: number, radius: number): string {
  const r = f1(radius);
  return `M${f1(x - r)} ${f1(y)}a${r} ${r} 0 1 0 ${f1(2 * r)} 0a${r} ${r} 0 1 0 ${f1(-2 * r)} 0`;
}

/** mulberry32 */
function rng(seed: number): () => number {
  let s = seed | 0;
  return () => {
    s = (s + 0x6d2b79f5) | 0;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function gauss(r: () => number): () => number {
  return () => Math.sqrt(-2 * Math.log(r() + 1e-9)) * Math.cos(2 * Math.PI * r());
}

/** The motif's path layers: a/b substrate (quiet/strong cells), h lit route, x other neurons, y other edges. */
export function motifPaths(opts: MotifOptions): { a: string; b: string; h: string; x: string; y: string } {
  const { width: W, height: H } = opts;
  const k = (opts.pitch ?? 28) / 18;
  const fx = (opts.fx ?? 0.7) * W;
  const fy = (opts.fy ?? 0.5) * H;
  const lit = opts.lit ?? true;
  const v = { a: '', b: '', h: '', x: '', y: '' };

  const c = 12 * k;
  const cols = Math.floor(W / c) + 1;
  const rows = Math.floor(H / c) + 1;
  const ox = (W - (cols - 1) * c) / 2;
  const oy = (H - (rows - 1) * c) / 2;
  const g = gauss(rng(opts.seed ?? 23));
  const val: number[][] = [];
  for (let r = 0; r < rows; r++) {
    const row: number[] = [];
    for (let col = 0; col < cols; col++) row.push(g());
    val.push(row);
  }

  const fc = Math.round((fx - ox) / c);
  const fr = Math.round((fy - oy) / c);
  const span = 14 + (LAYERS.length - 3) * 4;
  const c0 = fc - Math.floor(span / 2);
  const maxN = Math.max(...LAYERS);
  const sp = Math.max(1, Math.min(3, Math.floor((rows - 2) / Math.max(1, maxN - 1))));
  const L = LAYERS.map((n, li) => {
    const col = c0 + Math.round((li * span) / (LAYERS.length - 1));
    return Array.from({ length: n }, (_, i): [number, number] => [col, Math.round(fr + (i - (n - 1) / 2) * sp)]);
  });

  const inside = (q: [number, number]) => q[0] >= 0 && q[1] >= 0 && q[0] < cols && q[1] < rows;
  const vAt = (q: [number, number]) => (inside(q) ? Math.abs(val[q[1]]![q[0]]!) : 0);
  // The forward pass: in each layer the neuron on the strongest cell fires.
  const active = L.map((ns) => ns.reduce((best, q, i) => (vAt(q) > vAt(ns[best]!) ? i : best), 0));

  type Role = ['n' | 'e', 'h' | 'x' | 'y'];
  const role = new Map<string, Role>();
  const RANK = { h: 4, x: 2, y: 1 } as const;
  const set = (q: [number, number], kind: 'n' | 'e', cls: 'h' | 'x' | 'y') => {
    if (!inside(q)) return;
    const key = `${q[0]},${q[1]}`;
    const cur = role.get(key);
    if (cur && cur[0] === 'n' && kind === 'e') return;
    if (cur && cur[0] === kind && RANK[cur[1]] >= RANK[cls]) return;
    role.set(key, [kind, cls]);
  };
  const line = (a: [number, number], b: [number, number]) => {
    const n = Math.max(Math.abs(b[0] - a[0]), Math.abs(b[1] - a[1]));
    const out: Array<[number, number]> = [];
    for (let i = 1; i < n; i++) out.push([Math.round(a[0] + ((b[0] - a[0]) * i) / n), Math.round(a[1] + ((b[1] - a[1]) * i) / n)]);
    return out;
  };

  for (let li = 0; li < L.length - 1; li++) {
    L[li]!.forEach((a, i) =>
      L[li + 1]!.forEach((b, j) => {
        const hot = active[li] === i && active[li + 1] === j;
        line(a, b).forEach((q) => set(q, 'e', hot ? 'h' : 'y'));
      }),
    );
  }
  L.forEach((ns, li) => ns.forEach((q, i) => set(q, 'n', active[li] === i ? 'h' : 'x')));

  const rData = (m: number) => ((0.6 + Math.min(m / 2.5, 1) * 2.6) * c) / 12 * 0.9;
  const rFull = 0.42 * c;
  const rEdge = 0.26 * c;
  for (let r = 0; r < rows; r++) {
    for (let col = 0; col < cols; col++) {
      const x = ox + col * c;
      const y = oy + r * c;
      const m = Math.abs(val[r]![col]!);
      const ro = role.get(`${col},${r}`);
      if (!ro) {
        v[m < 0.9 ? 'a' : 'b'] += dot(x, y, rData(m));
        continue;
      }
      const d = dot(x, y, ro[0] === 'n' ? rFull : rEdge);
      if (!lit) v.b += d;
      else v[ro[1]] += d;
    }
  }
  return v;
}

/** The motif as a standalone <svg> element string. */
export function motifSvg(opts: MotifOptions): string {
  const { width: W, height: H, theme } = opts;
  const dim = opts.dim ?? 1;
  const col = COLOURS[theme];
  const op = OPACITY[theme];
  const o = (n: number) => String(Math.round(n * dim * 100) / 100);
  const v = motifPaths(opts);
  const id = opts.id ?? 'nr-motif';
  const mask = opts.mask && opts.mask !== 'none' ? MASKS[opts.mask] : null;

  const defs = mask
    ? `<defs><linearGradient id="${id}-g" x1="0" y1="0" x2="${mask.x2}" y2="${mask.y2}">${mask.stops
        .map(([offset, a]) => `<stop offset="${offset}" stop-color="#fff" stop-opacity="${a}"/>`)
        .join('')}</linearGradient><mask id="${id}-m"><rect width="${W}" height="${H}" fill="url(#${id}-g)"/></mask></defs>`
    : '';
  const layers = [
    `<path d="${v.a}" fill="${col.slate}" fill-opacity="${o(op.a)}"/>`,
    `<path d="${v.b}" fill="${col.slate}" fill-opacity="${o(op.b)}"/>`,
    `<path d="${v.h}" fill="${col.lit}" fill-opacity="${o(op.h)}"/>`,
    `<path d="${v.x}" fill="${col.neuron}" fill-opacity="${o(op.h)}"/>`,
    // In the orange motif the edge shade takes the lit opacity (canvas: op.y = op.h).
    `<path d="${v.y}" fill="${col.edge}" fill-opacity="${o(op.h)}"/>`,
  ].join('');
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${W}" height="${H}" viewBox="0 0 ${W} ${H}" preserveAspectRatio="xMidYMid slice" aria-hidden="true">${defs}<g${
    mask ? ` mask="url(#${id}-m)"` : ''
  }>${layers}</g></svg>`;
}
